import React, {useContext, useEffect, useState} from 'react';
import { TNtfyNew } from '../../_types/ntfy';
import { NtfyReport } from '../../features/store/ntfy.slice';
import { useDispatch } from 'react-redux';
import { FetchContext } from '../../features/contexts/fetch';
import moment from 'moment/moment';
import {
  CircularProgress,
  Divider,
  Grid,
  IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Table, TableBody,
  Paper,
  TableCell,
  TableContainer, TableHead,
  TablePagination, TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TablePaginationActions from '@mui/material/TablePagination/TablePaginationActions';
import StyledTableRow from '../StyledTableRow/StyledTableRow';
import { ThumbDownAlt, ThumbUpAlt } from '@mui/icons-material';
import { ConfirmByTypingDialog } from '../ConfirmByTypingDialog/ConfirmByTypingDialog';
import { ApproveAccountApplicationDialog } from '../ApproveAccountApplicationDialog/ApproveAccountApplicationDialog';
import { IApplication } from '../../_types/application';
import {PermissionSelection} from "../PermissionToggleList/PermissionToogleList";

interface HeadCell {
  disablePadding: boolean;
  id: string;
  label: string;
  numeric: boolean;
  isDate?: boolean;
  humanize?: boolean;
  width: number | string;
}

const headCells: readonly HeadCell[] = [
  {
    id: 'email',
    numeric: false,
    disablePadding: true,
    label: 'E-mail',
    width: 150,
  },
  {
    id: 'requestedPermissions',
    numeric: false,
    disablePadding: true,
    isDate: true,
    humanize: true,
    label: 'Site Access Requested',
    width: 175,
  }
];

interface RowAction {
  label: string;
  icon?: React.ReactNode;
  disabled?: boolean | ((row: any) => boolean);
  onClick: (row: any) => void | Promise<void>;
  color?: string;
}

const AccountApplicationTable: React.FC = (): React.JSX.Element => {
  const [applications, setApplications] = useState<IApplication[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [busy, setBusy] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLButtonElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<IApplication | null>(null);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approving, setApproving] = useState(false);
  const [denyDialogOpen, setDenyDialogOpen] = useState(false);
  const [denying, setDenying] = useState(false);
  const dispatch = useDispatch<any>();

  const fetchContext = useContext(FetchContext);

  const actions: readonly RowAction[] = [
    {
      label: "Approve Request",
      icon: <ThumbUpAlt />,
      color: 'green',
      onClick: (row) => {
        setApproveDialogOpen(true);
        handleCloseMenu();
      },
    },
    {
      'label': 'Deny Request',
      icon: <ThumbDownAlt />,
      color: 'red',
      onClick: (row) => {
        setDenyDialogOpen(true);
        handleCloseMenu();
      },
    },
  ];

  useEffect(() => {
    void (async () => {
      setBusy(true);
      setApplications([]);

      try {
        // @ts-ignore
        const { data } = await fetchContext.authAxios.get(
          `/pending_account_requests`,
        );

        setApplications(data);
      } catch (err: any) {
        const notification = TNtfyNew(
          err.message,
          'error',
          'long',
        );

        dispatch(NtfyReport(notification));
      }

      setBusy(false);
    })();
  }, []);

  const handleOpenMenu = (e: any, user: any) => {
    e.stopPropagation(); // don't trigger row onClick
    setMenuAnchor(e.currentTarget);
    setSelectedRow(user);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAction = async (action: RowAction) => {
    handleCloseMenu();
    if (!selectedRow) return;
    await action.onClick(selectedRow);
  };

  const handleApproval = async (application: IApplication | null, selectedPermissions: PermissionSelection, unselectedPermissions: PermissionSelection) => {
    if (application) {
      setApproving(true);

      const selected = { ... selectedPermissions };
      const unselected = { ... unselectedPermissions };

      try {
        // @ts-ignore
        await fetchContext.authAxios.post(
          '/pending_account_requests',
          {
            email: application.email,
            selectedPermissions: selected,
            unselectedPermissions: unselected,
            approved: true,
          },
        );

        applications.splice(applications.findIndex(c => c.email === application.email), 1);
      } catch (err: any) {
        const notification = TNtfyNew(
          err.message,
          'error',
          'long',
        );

        dispatch(NtfyReport(notification));
      }

      setApproving(false);
      setApproveDialogOpen(false);
    }
  }

  const confirmDeny = async () => {
    if (selectedRow) {
      setDenying(true);

      const application = selectedRow;

      try {
        // @ts-ignore
        await fetchContext.authAxios.post(
          '/pending_account_requests',
          {
            email: application.email,
            requestedPermissions: application.requestedPermissions,
            approved: false,
          },
        );

        applications.splice(applications.findIndex(c => c.email === application.email), 1);
      } catch (err: any) {
        const notification = TNtfyNew(
          err.message,
          'error',
          'long',
        );

        dispatch(NtfyReport(notification));
      }

      setDenying(false);
      setDenyDialogOpen(false);
    }
  };

  const getFieldValue = (application: IApplication, field: string, isDate: boolean, humanize: boolean) => {
    if (field === 'requestedPermissions') {
      return (
        <>
          {
            Object.keys(application.requestedPermissions).map((permission) => (
              <div key={permission}>
                {permission}
              </div>
            ))
          }
        </>
      )
    } else {
      const value = application[field as keyof typeof application];
      return (
        <>
          {
            isDate ?
              (value ? (humanize ? moment(value).fromNow() : moment(value).format('MM/DD/YYYY')) : '') :
              value
          }
        </>
      );
    }
  }

  const getTableRow = (application: IApplication) => (
    <>
      {headCells.map((headCell) => (
        <TableCell key={`${application.email}-${headCell.id}`}>
          {getFieldValue(application, headCell.id, headCell.isDate ?? false, headCell.humanize ?? false)}
        </TableCell>
      ))}

      <TableCell align="right" padding="checkbox">
        <Tooltip title="More actions">
          <IconButton
            size="small"
            aria-label="row actions"
            aria-controls={menuAnchor ? "row-actions-menu" : undefined}
            aria-haspopup="menu"
            aria-expanded={menuAnchor ? "true" : undefined}
            onClick={(e) => handleOpenMenu(e, application)}
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </>
  );

  return (
    <Paper
      elevation={3} sx={{
        p: 3, borderRadius: 2,
        bgcolor: (theme) => theme.palette.background.paper,
        mx: 'auto', mt: 1
      }}>
      {applications.length > 0 ? (
        <Grid container spacing={2} alignItems="center">
          {/* Title */}
          <Grid item xs={6}>
            <Typography variant="h6" sx={{ ml: 1 }}>
              Users
            </Typography>
          </Grid>

          {/* Pagination */}
          <Grid item xs={6} sx={{ textAlign: 'right' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              component="div"
              count={applications.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { 'aria-label': 'rows per page' },
                native: true,
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </Grid>

          {/* Table */}
          <Grid item xs={12}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {headCells.map((headCell, index) => (
                      <TableCell
                        key={headCell.id}
                        sx={{
                          width:
                            index !== headCells.length - 1
                              ? headCell.width
                              : 'auto',
                        }}
                      >
                        {headCell.label}
                      </TableCell>
                    ))}
                    <TableCell align="right" padding="checkbox" />
                  </TableRow>
                </TableHead>

                <TableBody>
                  {applications.map((application) => (
                    <StyledTableRow key={application.email}>
                      {getTableRow(application)}
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Row actions menu */}
              <Menu
                id="row-actions-menu"
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleCloseMenu}
                container={menuAnchor?.ownerDocument?.body}
                keepMounted
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {actions.map((action, idx) => {
                  const disabled =
                    typeof action.disabled === 'function'
                      ? !!(selectedRow && action.disabled(selectedRow))
                      : !!action.disabled;

                  return action.label !== '-' ? (
                    <MenuItem
                      key={idx}
                      onClick={() => handleAction(action)}
                      disabled={disabled}
                    >
                      {action.icon && (
                        <ListItemIcon sx={{ color: action.color || 'inherit' }}>
                          {action.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText sx={{ color: action.color || 'inherit' }}>
                        {action.label}
                      </ListItemText>
                    </MenuItem>
                  ) : (
                    <Divider key={idx} />
                  );
                })}
              </Menu>
            </TableContainer>
          </Grid>
        </Grid>
      ) : (
        busy && (
          <Typography align="center">
            <CircularProgress />
          </Typography>
        )
      )}

      {/* Approve dialog */}
      <ApproveAccountApplicationDialog
        application={selectedRow}
        open={approveDialogOpen}
        loading={approving}
        onClose={() => setApproveDialogOpen(false)}
        onSave={handleApproval}
      />

      {/* Deny dialog */}
      <ConfirmByTypingDialog
        open={denyDialogOpen}
        expectedText={selectedRow?.email ?? ''}
        title="Deny account request"
        message={
          selectedRow && (
            <>
              This action cannot be undone. Denying{' '}
              <strong>{selectedRow.email}</strong> will discard their account
              request.
            </>
          )
        }
        confirmLabel="Deny Request"
        onClose={() => setDenyDialogOpen(false)}
        onConfirm={confirmDeny}
        loading={denying}
      />
    </Paper>
  );
};

export default AccountApplicationTable;
